export type DirectOnvifOnboardingStage =
  | "create"
  | "profile-validation"
  | "enable";

export type DirectOnvifOnboardingFailure = Error & {
  stage: DirectOnvifOnboardingStage;
  cameraId?: string;
  cameraCreated: boolean;
  metadataPersisted: boolean;
};

export type DirectOnvifOnboardingResult = {
  cameraId: string;
  createResponse: unknown;
  profileResponse: unknown;
  stateResponse: unknown;
  stateStatus: number;
  placementPending: boolean;
};

type FetchResponse = Pick<
  Response,
  "ok" | "status" | "statusText" | "text"
>;

type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<FetchResponse>;

type DirectOnvifOnboardingInput = {
  apiBase: string;
  idToken: string;
  profileToken: string;
  createPayload: Record<string, unknown>;
  fetchImpl?: FetchLike;
};

type ParsedResponse = {
  text: string;
  payload: unknown;
};

type StepResponse = ParsedResponse & {
  status: number;
};

const ERROR_NAME = "DirectOnvifOnboardingError";

function asRecord(value: unknown): Record<string, any> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, any>)
    : null;
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!text) return value;
  try {
    return JSON.parse(text);
  } catch {
    return value;
  }
}

function payloadLayers(payload: unknown): unknown[] {
  const layers: unknown[] = [];
  let current = parseMaybeJson(payload);

  for (let index = 0; index < 4; index += 1) {
    layers.push(current);
    const record = asRecord(current);
    if (!record || record.body === undefined) break;
    const next = parseMaybeJson(record.body);
    if (next === current) break;
    current = next;
  }

  return layers;
}

function normalizedId(value: unknown): string {
  const attributeValue = asRecord(value);
  const normalizedValue =
    attributeValue?.S ?? attributeValue?.N ?? attributeValue?.value ?? value;
  const text = String(normalizedValue ?? "").trim();
  if (!text) return "";
  const cameraKey = /^CAMERA#(.+)$/i.exec(text);
  return (cameraKey?.[1] || text).trim();
}

export function cameraIdFromCreateResponse(payload: unknown): string {
  for (const layer of payloadLayers(payload)) {
    const record = asRecord(layer);
    if (!record) continue;

    const camera = asRecord(record.camera);
    const item = asRecord(record.item);
    const data = asRecord(record.data);
    const dataCamera = asRecord(data?.camera);

    const candidates = [
      record.cameraId,
      record.id,
      camera?.cameraId,
      camera?.id,
      item?.cameraId,
      item?.id,
      data?.cameraId,
      data?.id,
      dataCamera?.cameraId,
      dataCamera?.id,
      record.SK,
      camera?.SK,
      item?.SK,
      data?.SK,
      dataCamera?.SK,
    ];

    for (const candidate of candidates) {
      const cameraId = normalizedId(candidate);
      if (cameraId) return cameraId;
    }
  }

  return "";
}

function payloadMessage(payload: unknown): string {
  for (const layer of payloadLayers(payload)) {
    const record = asRecord(layer);
    if (!record) continue;
    for (const candidate of [
      record.message,
      record.error,
      asRecord(record.details)?.message,
    ]) {
      const text = String(candidate ?? "").trim();
      if (text) return text;
    }
  }
  return "";
}

function payloadBoolean(payload: unknown, field: string): boolean {
  for (const layer of payloadLayers(payload)) {
    const record = asRecord(layer);
    if (!record) continue;
    if (record[field] === true) return true;
    const data = asRecord(record.data);
    if (data?.[field] === true) return true;
  }
  return false;
}

async function readResponse(response: FetchResponse): Promise<ParsedResponse> {
  const text = await response.text();
  return {
    text,
    payload: parseMaybeJson(text),
  };
}

function failure(
  stage: DirectOnvifOnboardingStage,
  message: string,
  options: {
    cameraId?: string;
    cameraCreated: boolean;
    metadataPersisted: boolean;
  },
): DirectOnvifOnboardingFailure {
  const error = new Error(message) as DirectOnvifOnboardingFailure;
  error.name = ERROR_NAME;
  error.stage = stage;
  error.cameraId = options.cameraId;
  error.cameraCreated = options.cameraCreated;
  error.metadataPersisted = options.metadataPersisted;
  return error;
}

function unexpectedMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : String(error || "Unexpected network error");
}

async function requestStep({
  fetchImpl,
  url,
  init,
  stage,
  label,
  cameraId,
  cameraCreated,
  metadataPersisted,
}: {
  fetchImpl: FetchLike;
  url: string;
  init: RequestInit;
  stage: DirectOnvifOnboardingStage;
  label: string;
  cameraId?: string;
  cameraCreated: boolean;
  metadataPersisted: boolean;
}): Promise<StepResponse> {
  let response: FetchResponse;
  try {
    response = await fetchImpl(url, init);
  } catch (error) {
    throw failure(stage, `${label}: ${unexpectedMessage(error)}`, {
      cameraId,
      cameraCreated,
      metadataPersisted,
    });
  }

  let parsed: ParsedResponse;
  try {
    parsed = await readResponse(response);
  } catch (error) {
    throw failure(
      stage,
      `${label}: unable to read the API response (${unexpectedMessage(error)})`,
      {
        cameraId,
        cameraCreated,
        metadataPersisted,
      },
    );
  }

  if (!response.ok) {
    const detail =
      payloadMessage(parsed.payload) ||
      parsed.text.trim() ||
      response.statusText ||
      "Request failed";
    throw failure(stage, `${label} (${response.status}): ${detail}`, {
      cameraId,
      cameraCreated,
      metadataPersisted,
    });
  }

  return {
    ...parsed,
    status: response.status,
  };
}

export function isDirectOnvifOnboardingFailure(
  value: unknown,
): value is DirectOnvifOnboardingFailure {
  return (
    value instanceof Error &&
    value.name === ERROR_NAME &&
    ["create", "profile-validation", "enable"].includes(
      String((value as Partial<DirectOnvifOnboardingFailure>).stage || ""),
    )
  );
}

export async function onboardDirectOnvifCamera({
  apiBase,
  idToken,
  profileToken,
  createPayload,
  fetchImpl = fetch,
}: DirectOnvifOnboardingInput): Promise<DirectOnvifOnboardingResult> {
  const base = String(apiBase || "").trim().replace(/\/+$/, "");
  const token = String(idToken || "").trim();
  const selectedProfileToken = String(profileToken || "").trim();

  if (!base) {
    throw failure("create", "Camera API base URL is missing", {
      cameraCreated: false,
      metadataPersisted: false,
    });
  }
  if (!token) {
    throw failure("create", "Authentication session is missing", {
      cameraCreated: false,
      metadataPersisted: false,
    });
  }
  if (!selectedProfileToken) {
    throw failure("create", "Select an ONVIF profile before adding the camera", {
      cameraCreated: false,
      metadataPersisted: false,
    });
  }

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const createStep = await requestStep({
    fetchImpl,
    url: base,
    init: {
      method: "POST",
      headers,
      body: JSON.stringify({
        ...createPayload,
        // Fail closed: an incomplete record must never be assigned to a Media Gateway.
        enabled: false,
      }),
    },
    stage: "create",
    label: "Unable to create the disabled ONVIF camera",
    cameraCreated: false,
    metadataPersisted: false,
  });

  const createResponse = createStep.payload;
  const cameraId = cameraIdFromCreateResponse(createResponse);
  if (!cameraId) {
    throw failure(
      "create",
      "The camera was created disabled, but the camera API did not return its camera ID",
      {
        cameraCreated: true,
        metadataPersisted: false,
      },
    );
  }

  const encodedCameraId = encodeURIComponent(cameraId);
  const profileStep = await requestStep({
    fetchImpl,
    url: `${base}/${encodedCameraId}/video-settings`,
    init: {
      method: "PATCH",
      headers,
      body: JSON.stringify({
        profileToken: selectedProfileToken,
        // The backend reads the current encoder values from ONVIF, verifies H.264,
        // and persists the complete videoSettings record without changing quality.
        settings: {},
      }),
    },
    stage: "profile-validation",
    label: "Unable to validate and save the selected H.264 profile",
    cameraId,
    cameraCreated: true,
    metadataPersisted: false,
  });

  const stateStep = await requestStep({
    fetchImpl,
    url: `${base}/${encodedCameraId}/state`,
    init: {
      method: "PATCH",
      headers,
      body: JSON.stringify({ enabled: true }),
    },
    stage: "enable",
    label: "H.264 was saved, but the camera could not be enabled",
    cameraId,
    cameraCreated: true,
    metadataPersisted: true,
  });

  return {
    cameraId,
    createResponse,
    profileResponse: profileStep.payload,
    stateResponse: stateStep.payload,
    stateStatus: stateStep.status,
    placementPending:
      stateStep.status === 202 ||
      payloadBoolean(stateStep.payload, "placementPending") ||
      payloadBoolean(stateStep.payload, "scaleOutInProgress"),
  };
}
